import { computed, ref } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineOpportunitiesAPI from 'dashboard/api/pipeline/opportunities';
import {
  formatLocalizedCurrency,
  formatLocalizedDateInTimezone,
  resolveReportingTimezone,
} from '../utils/localeFormatting';

const PAGE_SIZE = 50;

/**
 * Histórico financeiro de um card (Fase 59), compartilhado pelas DUAS
 * superfícies que o exibem: o modal aberto pelo board e a aba dentro do card.
 *
 * Elas nasceram com cópias separadas da mesma carga paginada, validação de
 * payload, invalidação de requisição e formatação — e as cópias já tinham
 * começado a divergir: cada correção (data no idioma da conta, falha de
 * paginação preservando a página carregada) precisou ser feita duas vezes, e a
 * segunda quase ficou para trás. Aqui a regra existe uma vez só.
 *
 * @param {import('vue').Ref<Object>} card - ref do card exibido
 * @returns {Object} estado e helpers do histórico
 */
export function useOpportunityHistory(card) {
  const { t, locale } = useI18n();
  const store = useStore();

  const opportunities = ref([]);
  const summary = ref(null);
  const loading = ref(false);
  const loadingMore = ref(false);
  // ⚠️ Sinalizador, NÃO a string traduzida: `t()` dentro do catch congela o
  // texto no idioma daquele instante, e o locale da conta só é aplicado depois
  // do boot — uma falha na primeira carga ficava em inglês para sempre num app
  // em pt-BR. Traduzir no template mantém a mensagem reativa.
  const loadFailed = ref(false);
  const appendFailed = ref(false);

  // Cada carga invalida a anterior: sem isso, trocar de card rápido deixaria a
  // lista do card antigo pintada no novo.
  let requestId = 0;

  const currencyCode = computed(
    () => summary.value?.currency || card.value?.currency || 'BRL'
  );

  const formatMoney = value => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    try {
      return formatLocalizedCurrency(number, {
        currency: currencyCode.value,
        locale: locale.value,
      });
    } catch {
      return `${currencyCode.value} ${number}`;
    }
  };

  // Data no fuso da CONTA e no idioma da conta. No fuso do navegador, uma venda
  // perto da meia-noite cairia no dia financeiro errado; sem locale (o que
  // `formatInReportingTimezone` faz) sairia "04 Aug 2026" numa tela em pt-BR.
  const formatDate = value =>
    formatLocalizedDateInTimezone(value, {
      locale: locale.value,
      timeZone: resolveReportingTimezone(store),
      dateStyle: 'medium',
    });

  // Contagens vêm do SERVIDOR: derivá-las da página faria "N estornadas"
  // discordar do total assim que o histórico passasse de uma página.
  const recordedCount = computed(() => Number(summary.value?.count ?? 0));
  const voidedCount = computed(() => Number(summary.value?.voided_count ?? 0));
  const historyCount = computed(() =>
    Number(
      summary.value?.history_count ?? recordedCount.value + voidedCount.value
    )
  );

  // Quantas linhas a LISTA tem, segundo a própria resposta que a paginou.
  // O resumo vem dos contadores denormalizados do card, que podem estar
  // atrasados: confiar neles esconderia o "carregar mais" e truncaria a trilha
  // de auditoria — ou ofereceria o botão para sempre.
  const listedCount = ref(null);
  const hasMore = computed(() => {
    const total = listedCount.value ?? historyCount.value;
    return opportunities.value.length < Number(total || 0);
  });
  const totalValue = computed(() => summary.value?.total ?? 0);
  const averageTicket = computed(() => {
    if (!recordedCount.value) return 0;
    return Number(totalValue.value) / recordedCount.value;
  });

  const isEmpty = computed(
    () =>
      !loading.value && !loadFailed.value && opportunities.value.length === 0
  );

  // Um 200 com shape inesperado NÃO pode virar "histórico vazio, total zero":
  // isso afirmaria que o cliente nunca comprou. Payload malformado é falha de
  // carga, igual a uma rejeição de rede.
  const isValidPayload = data =>
    Array.isArray(data?.opportunities) &&
    data?.summary !== null &&
    typeof data?.summary === 'object' &&
    data.summary.total !== undefined &&
    data.summary.count !== undefined;

  const load = async ({ append = false } = {}) => {
    requestId += 1;
    const currentRequest = requestId;
    if (append) loadingMore.value = true;
    else loading.value = true;
    // Falha ao paginar não derruba o que já está na tela: apagar a primeira
    // página levaria junto o total financeiro já conferido.
    if (append) appendFailed.value = false;
    else loadFailed.value = false;
    try {
      const { data } = await PipelineOpportunitiesAPI.list(card.value.id, {
        perPage: PAGE_SIZE,
        offset: append ? opportunities.value.length : 0,
      });
      if (currentRequest !== requestId) return;
      if (!isValidPayload(data)) {
        if (append) appendFailed.value = true;
        else loadFailed.value = true;
        return;
      }
      opportunities.value = append
        ? [...opportunities.value, ...data.opportunities]
        : data.opportunities;
      summary.value = data.summary;
      const total = Number(data.meta?.total_count);
      listedCount.value = Number.isFinite(total) ? total : null;
    } catch {
      if (currentRequest !== requestId) return;
      if (append) appendFailed.value = true;
      else loadFailed.value = true;
    } finally {
      // `return` dentro de finally engole exceções; o guard vira condição.
      if (currentRequest === requestId) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const loadMore = () => load({ append: true });

  // Estorno mora aqui, junto da carga: quem estorna precisa que a lista e o
  // total voltem do SERVIDOR (o acumulado é recalculado lá), não de um ajuste
  // otimista no cliente que poderia divergir do razão.
  const voiding = ref(null);
  const voidFailed = ref(null);

  // Guarda de QUAL linha o estorno falhou, não um booleano global: cancelar e
  // abrir outra venda mostrava o erro da anterior antes de qualquer requisição.
  // Permissão e o passo-a-passo da confirmação vivem AQUI, não em cada tela: o
  // modal do board e a aba do card mostram o mesmo histórico, e duplicar o
  // recorte de autorização é como as duas divergem sem ninguém notar.
  const { checkPermissions } = usePolicy();
  const canVoid = computed(() =>
    checkPermissions(['administrator', 'pipeline_manage'])
  );

  const voidingEntry = ref(null);
  const voidReason = ref('');

  const canVoidEntry = entry =>
    canVoid.value &&
    entry.status !== 'voided' &&
    voidingEntry.value !== entry.id;

  const startVoid = entry => {
    voidingEntry.value = entry.id;
    voidReason.value = '';
    voidFailed.value = null;
  };

  const cancelVoid = () => {
    voidingEntry.value = null;
    voidReason.value = '';
    voidFailed.value = null;
  };

  const voidSale = async (entry, reason) => {
    voiding.value = entry.id;
    voidFailed.value = null;
    try {
      await PipelineOpportunitiesAPI.void(entry.id, { reason });
    } catch {
      voidFailed.value = entry.id;
      voiding.value = null;
      return false;
    }

    // Recarga em bloco SEPARADO: o estorno já foi commitado e é irreversível.
    // Juntar os dois num try só fazia uma falha de rede na releitura ser
    // relatada como "não foi possível estornar" — e o operador tentaria de
    // novo uma operação que já aconteceu.
    try {
      await load();
    } catch {
      loadFailed.value = true;
    } finally {
      // Só agora: liberar antes da releitura reabriria a confirmação com os
      // dados velhos ainda na tela.
      voiding.value = null;
    }
    return true;
  };

  const confirmVoid = async entry => {
    const done = await voidSale(entry, voidReason.value.trim());
    if (done) cancelVoid();
  };

  const reset = () => {
    opportunities.value = [];
    summary.value = null;
    listedCount.value = null;
    voiding.value = null;
    voidFailed.value = null;
    // Zera também os erros: sem isto, uma falha de "carregar mais" num card
    // continuaria pintada ao abrir OUTRO card, com um botão "tentar de novo"
    // que não se refere a nada do que está na tela.
    loadFailed.value = false;
    appendFailed.value = false;
  };

  // Venda migrada pode não ter nome: o backfill grava título NULO em vez de
  // fixar um idioma no banco. Quem lê traduz.
  const entryTitle = entry =>
    entry.title || t('PIPELINE.OPPORTUNITIES.UNTITLED_SALE');

  // Estorno mexe em dinheiro: quem estornou e quando são parte da trilha, não
  // detalhe opcional.
  const voidLine = entry => {
    const reason = entry.void_reason || t('PIPELINE.OPPORTUNITIES.NO_REASON');
    const parts = [t('PIPELINE.OPPORTUNITIES.VOIDED_WITH_REASON', { reason })];
    if (entry.voided_by_name) parts.push(entry.voided_by_name);
    const when = formatDate(entry.voided_at);
    if (when) parts.push(when);
    return parts.join(' · ');
  };

  // Autor + ciclo. Backfill não tem autor (não sabe quem vendeu), então a linha
  // simplesmente não o menciona em vez de inventar um nome.
  const rowMeta = entry => {
    const parts = [];
    if (entry.recorded_by_name) parts.push(entry.recorded_by_name);
    if (entry.cycle_number) {
      parts.push(
        t('PIPELINE.OPPORTUNITIES.CYCLE', { number: entry.cycle_number })
      );
    }
    return parts.join(' · ');
  };

  return {
    opportunities,
    summary,
    loading,
    loadingMore,
    loadFailed,
    appendFailed,
    currencyCode,
    recordedCount,
    voidedCount,
    historyCount,
    hasMore,
    totalValue,
    averageTicket,
    isEmpty,
    formatMoney,
    formatDate,
    entryTitle,
    voidLine,
    rowMeta,
    load,
    loadMore,
    reset,
    voidSale,
    voiding,
    voidFailed,
    canVoidEntry,
    voidingEntry,
    voidReason,
    startVoid,
    cancelVoid,
    confirmVoid,
  };
}
